/*
 * Save for later -> "My designs" in the customer account. Kept out of
 * dyof-configurator.js on purpose: remove this file's script tag and the
 * data-dyof-account-email attribute from sections/dyof-configurator.liquid and
 * the module is exactly as before.
 *
 * How a save is recognised: when Save for later reaches the configuration
 * service the module shows its success outcome (<p data-kind="success">, the
 * "Saved securely..." text) in [data-dyof-save-status] and, in the same task,
 * puts the saved link's ID in the URL fragment (#dyof_saved=dyof_...). Only
 * that outcome counts; browser-only saves and refusals use other kinds.
 *
 * Signed in (data-dyof-account-email holds customer.email): the design is
 * filed in the existing saving engine (the one snippets/saving.liquid uses)
 *   POST .../api/user/order { email, URL: saved link, name, date, id }
 * name = the DYOF service's auto-name (POST .../configurations/summary) or
 * "My design"; id stays the same for every re-save of one design (see
 * recordIdFor), so the engine updates its link and date and keeps any name the
 * customer gave it. Then a line "Saved to your account - see My designs."
 * Signed out: "Sign in to keep it in your account."; following that link keeps
 * the design in this tab's sessionStorage and files it once the shopper is
 * back here signed in.
 * Every successful filing is followed by POST .../configurations/keep
 * { saved }, which keeps a design in an account for two years: fire and
 * forget, one retry, and an older service's 404/405 ignored.
 * The module's save is never delayed, changed or blocked; failures are silent.
 */
(function (root) {
  "use strict";

  var ENGINE_SAVE = "https://secure-scrubland-64699-70cffcfa4865.herokuapp.com/api/user/order";
  var SAVED_ID = /^dyof_[a-f0-9]{24}$/;
  var MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  // Saved ID -> engine record id; assets/dyof-my-designs.js adds every design
  // the account page lists, so a design opened from there keeps its record.
  var RECORDS_KEY = "dyof:account-save:records";
  var PENDING_KEY = "dyof:account-save:pending";
  var PENDING_MAX_AGE_MS = 6 * 60 * 60 * 1000;
  var SUMMARY_TIMEOUT_MS = 4000;
  var KEEP_RETRY_MS = 3000;

  // Translations follow the module (assets/dyof-i18n.js, non-English stores only).
  function dyofT(key, english, params) {
    var i18n = root.DYOFI18n;
    if (i18n) return i18n.t(key, english, params);
    return params ? String(english).replace(/\{([A-Za-z0-9_]+)\}/g, function (match, name) {
      return Object.prototype.hasOwnProperty.call(params, name) && params[name] != null ? String(params[name]) : match;
    }) : english;
  }

  function shopRoot() {
    var routes = root.Shopify && root.Shopify.routes;
    var base = routes && typeof routes.root === "string" ? routes.root : "/";
    return base.charAt(base.length - 1) === "/" ? base : base + "/";
  }

  function savedIdFromUrl() {
    try {
      var hash = new URLSearchParams(String(root.location.hash || "").replace(/^#\??/, ""));
      var id = hash.get("dyof_saved");
      return SAVED_ID.test(String(id || "")) ? id : "";
    } catch (error) {
      return "";
    }
  }

  function savedLink(savedId) {
    var origin = /^https:\/\//.test(root.location.origin) ? root.location.origin : "https://filofax.com";
    return origin + "/products/design-your-own-refill?dyof_saved=" + encodeURIComponent(savedId);
  }

  function engineDate() {
    var d = new Date();
    return d.getDate() + " " + MONTHS[d.getMonth()] + " " + d.getFullYear();
  }

  function readRecords() {
    try {
      var map = JSON.parse(root.localStorage.getItem(RECORDS_KEY) || "{}");
      return map && typeof map === "object" ? map : {};
    } catch (error) {
      return {};
    }
  }

  function rememberRecord(savedId, recordId) {
    try {
      var map = readRecords();
      delete map[savedId];
      map[savedId] = recordId;
      var keys = Object.keys(map);
      keys.slice(0, Math.max(0, keys.length - 100)).forEach(function (key) { delete map[key]; });
      root.localStorage.setItem(RECORDS_KEY, JSON.stringify(map));
    } catch (error) {
      // Storage may be off; the next re-save then starts its own record.
    }
  }

  // Every save gets a new configuration ID, so the record is named after the
  // first one in this design's line: the link it was opened from, or its
  // first save here. A design listed in My designs keeps that record's id.
  function recordIdFor(previousId, savedId) {
    var known = previousId ? readRecords()[previousId] : "";
    return typeof known === "string" && known ? known : "dyof_" + (previousId || savedId);
  }

  function configurationEndpoint(moduleEl) {
    var host = root.location.hostname;
    var local = host === "127.0.0.1" || host === "localhost";
    return (local && moduleEl.getAttribute("data-dev-configuration-endpoint"))
      || moduleEl.getAttribute("data-configuration-endpoint") || "";
  }

  function summaryEndpoint(moduleEl) {
    var base = configurationEndpoint(moduleEl);
    return base ? base + "/summary" : "";
  }

  // Now in the account: kept for two years. Never awaited.
  function keepDesign(moduleEl, savedId) {
    var base = configurationEndpoint(moduleEl);
    if (!base || !SAVED_ID.test(String(savedId || ""))) return;
    var tries = 0;
    function attempt() {
      tries += 1;
      var again = function () { if (tries < 2) root.setTimeout(attempt, KEEP_RETRY_MS); };
      try {
        // The retry drops keepalive, in case a browser refuses it here.
        root.fetch(base + "/keep", {
          method: "POST",
          keepalive: tries === 1,
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          body: JSON.stringify({ saved: savedId })
        }).then(function (response) {
          // 404/405 is a service without the route yet; other refusals stay.
          if (response.status >= 500 || response.status === 429) again();
        }, again);
      } catch (error) {
        again();
      }
    }
    attempt();
  }

  function autoName(moduleEl, savedId) {
    var endpoint = summaryEndpoint(moduleEl);
    if (!endpoint) return Promise.resolve("");
    var controller = typeof root.AbortController === "function" ? new root.AbortController() : null;
    var timer = controller ? root.setTimeout(function () { controller.abort(); }, SUMMARY_TIMEOUT_MS) : null;
    // POST keeps the saved id out of request URLs and logs.
    return root.fetch(endpoint, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ saved: savedId }),
      signal: controller ? controller.signal : undefined
    }).then(function (response) {
      return response.ok ? response.json() : null;
    }).then(function (data) {
      return data && typeof data.autoName === "string" ? data.autoName.trim().slice(0, 100) : "";
    }).catch(function () {
      return "";
    }).then(function (name) {
      if (timer) root.clearTimeout(timer);
      return name;
    });
  }

  function fileDesign(moduleEl, email, savedId, recordId) {
    return autoName(moduleEl, savedId).then(function (name) {
      return root.fetch(ENGINE_SAVE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email,
          URL: savedLink(savedId),
          name: name || "My design",
          date: engineDate(),
          id: recordId
        })
      });
    }).then(function (response) {
      var ok = Boolean(response && response.ok);
      if (ok) keepDesign(moduleEl, savedId);
      return ok;
    }, function () {
      return false;
    });
  }

  function readPending() {
    try {
      var value = JSON.parse(root.sessionStorage.getItem(PENDING_KEY) || "null");
      if (!value || !SAVED_ID.test(String(value.savedId || "")) || typeof value.recordId !== "string") return null;
      if (!(Date.now() - Number(value.at) < PENDING_MAX_AGE_MS)) return null;
      return value;
    } catch (error) {
      return null;
    }
  }

  function writePending(value) {
    try {
      if (value) root.sessionStorage.setItem(PENDING_KEY, JSON.stringify(value));
      else root.sessionStorage.removeItem(PENDING_KEY);
    } catch (error) {
      // Storage may be off; the shopper can save again after signing in.
    }
  }

  function setUp(moduleEl) {
    var region = moduleEl.querySelector("[data-dyof-save-status]");
    if (!region || typeof root.MutationObserver !== "function" || typeof root.fetch !== "function") return;
    var email = String(moduleEl.getAttribute("data-dyof-account-email") || "").trim();
    var filed = {};
    // The saved link this design came from: the page's link, then each save.
    var previousId = savedIdFromUrl();

    // A Save click starts from whatever link the page shows now (none after
    // the module starts a new design).
    moduleEl.addEventListener("click", function (event) {
      if (event.target && event.target.closest && event.target.closest("[data-dyof-save]")) {
        previousId = savedIdFromUrl();
      }
    }, true);

    // A sibling under the module's outcome paragraph, which stays the region's
    // first child: the module compares only that one with its own text, and
    // clears the region (this line included) when the design changes.
    function showLine(outcome, template, linkText, href, onFollow) {
      if (!region.contains(outcome)) return;
      var old = region.querySelector("[data-dyof-account-save]");
      if (old) old.remove();
      var line = document.createElement("p");
      line.setAttribute("data-dyof-account-save", "");
      line.dataset.kind = "account";
      var parts = String(template).split("{link}");
      var link = document.createElement("a");
      link.href = href;
      link.textContent = linkText;
      if (onFollow) link.addEventListener("click", onFollow);
      line.appendChild(document.createTextNode(parts[0]));
      line.appendChild(link);
      line.appendChild(document.createTextNode(parts.slice(1).join("{link}")));
      region.appendChild(line);
    }

    function showSaved(outcome) {
      showLine(
        outcome,
        dyofT("account.save.saved", "Saved to your account - see {link}."),
        dyofT("account.save.my_designs", "My designs"),
        shopRoot() + "account#my-designs"
      );
    }

    function showSignIn(outcome, savedId, recordId) {
      // The saved ID is a restore capability and stays out of the sign-in URL.
      var back = root.location.pathname + root.location.search;
      showLine(
        outcome,
        dyofT("account.save.sign_in", "{link} to keep it in your account."),
        dyofT("account.save.sign_in_link", "Sign in"),
        shopRoot() + "account/login?return_url=" + encodeURIComponent(back),
        function () { writePending({ savedId: savedId, recordId: recordId, at: Date.now() }); }
      );
    }

    function handleSaved(outcome) {
      var savedId = savedIdFromUrl()
        || (SAVED_ID.test(String(moduleEl.baseConfigurationId || "")) ? moduleEl.baseConfigurationId : "");
      if (!savedId || !region.contains(outcome)) return;
      var entry = filed[savedId];
      if (!entry) {
        var recordId = recordIdFor(previousId !== savedId ? previousId : "", savedId);
        rememberRecord(savedId, recordId);
        previousId = savedId;
        entry = filed[savedId] = { recordId: recordId, result: null };
      }
      if (!email) {
        showSignIn(outcome, savedId, entry.recordId);
        return;
      }
      if (!entry.result) {
        entry.result = fileDesign(moduleEl, email, savedId, entry.recordId);
        // A failed filing is tried again, under the same record, on the next outcome.
        entry.result.then(function (ok) { if (!ok) entry.result = null; });
      }
      entry.result.then(function (ok) { if (ok) showSaved(outcome); });
    }

    new root.MutationObserver(function (records) {
      records.forEach(function (record) {
        Array.prototype.forEach.call(record.addedNodes, function (node) {
          if (node.nodeType !== 1 || node.hasAttribute("data-dyof-account-save")) return;
          if (node.getAttribute("data-kind") !== "success") return;
          // The module sets #dyof_saved right after showing the outcome.
          root.setTimeout(function () { handleSaved(node); }, 0);
        });
      });
    }).observe(region, { childList: true });

    // Back from "Sign in to keep it in your account": file the waiting design.
    var pending = email ? readPending() : null;
    if (pending) {
      writePending(null);
      fileDesign(moduleEl, email, pending.savedId, pending.recordId);
    }
  }

  function start() {
    Array.prototype.forEach.call(document.querySelectorAll("dyof-configurator[data-dyof-account-email]"), function (moduleEl) {
      if (moduleEl.hasAttribute("data-dyof-account-save-ready")) return;
      moduleEl.setAttribute("data-dyof-account-save-ready", "");
      setUp(moduleEl);
    });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();
})(typeof window !== "undefined" ? window : this);
